import * as Dialog from '@radix-ui/react-dialog';
import { Dispatch, SetStateAction } from 'react';

import {
  RiskLevelIcon,
  UnknownIcon,
  PositiveIcon,
  AttentionIcon,
} from '@/components/TokenProfile/TokenProfileSecurityAudit/RiskIcons/RiskLevelIcons';
import {
  AlertInfo,
  calculateSecurityAlerts,
  calculateSolanaSecurityAlerts,
} from '@/components/TokenProfile/TokenProfileSecurityAudit/utils';
import { CopyAddress } from '@/components/TransactionDetails/CopyAddress';

import { CHAIN_IDS } from '@/constants/chain';

import { useSolanaTokenSecurityInfo } from '@/hooks/useSolanaTokenSecurity';

import { buttons } from '@/styles/buttons';
import { fontStyles } from '@/styles/typography';

import { InfoCircleSolidSmallIcon } from '@/ui/Icons/InfoCircleSolidSmallIcon';
import { XIcon } from '@/ui/Icons/XIcon';
import {
  MatchaTooltip,
  MatchaTooltipBottomSheet,
  MatchaTooltipContent,
  MatchaTooltipPopover,
  MatchaTooltipTrigger,
} from '@/ui/MatchaTooltip';
import { Modal } from '@/ui/Modal';

import { TokenResult } from '@/utils/0x/token-registry.types';
import { isTokenResultNativeAsset } from '@/utils/multichain';

interface Props {
  activeToken: TokenResult;
  isOpen: boolean;
  onOpenChange: Dispatch<SetStateAction<boolean>>;
}
interface AlertSectionProps {
  activeToken: TokenResult;
  title: string;
  alerts: AlertInfo[];
}

const AlertSection: React.FC<AlertSectionProps> = ({ activeToken, title, alerts }) => {
  if (!alerts || alerts.length === 0) return null;
  const sourceCode = getSourceContractLink(activeToken);
  return (
    <div className="pt-4 overflow-y-auto pb-6 flex-1">
      <div className="flex items-center my-4 justify-between">
        <h3
          className={fontStyles({ variant: 'LABEL_MD', class: ['pl-2.5', 'text-token-gray-800'] })}
        >
          {title}
        </h3>
      </div>

      <div className="flex flex-col overflow-x-hidden">
        {alerts.map((alert, index) => (
          <div
            key={index}
            className="flex items-center justify-between py-3 border-t-[1px] min-h-[48px] border-token-gray-300"
          >
            <MatchaTooltip delayDuration={0}>
              <MatchaTooltipTrigger>
                <div className="flex items-center gap-2 pl-2.5">
                  <span
                    className={fontStyles({ variant: 'BODY_MD', class: ['text-token-gray-900'] })}
                  >
                    {alert.label}
                  </span>
                  <InfoCircleSolidSmallIcon
                    width={16}
                    height={16}
                    className="text-token-gray-900"
                  />
                </div>
              </MatchaTooltipTrigger>
              <MatchaTooltipContent>
                <p>{alert.tooltipInfo}</p>
              </MatchaTooltipContent>
              <MatchaTooltipPopover align="end" alignOffset={-142.5} side="top" />
              <MatchaTooltipBottomSheet />
            </MatchaTooltip>
            <div className="flex items-center gap-2 pr-2.5">
              {alert.type === 'address' ? (
                <CopyAddress address={alert.valueLabel} />
              ) : (
                <span
                  className={fontStyles({ variant: 'BODY_MD', class: ['text-token-gray-900'] })}
                >
                  {alert.valueLabel}
                </span>
              )}
              {alert.label === 'Verified Source Code' && !isTokenResultNativeAsset(activeToken) ? (
                <>
                  ·
                  <a
                    className={fontStyles({
                      variant: 'BODY_MD',
                      class: ['text-token-gray-800', 'mr-2', 'hover:underline'],
                    })}
                    href={sourceCode.href}
                    target="_blank"
                  >
                    {sourceCode.label}
                  </a>
                </>
              ) : null}
              {alert.type === 'risk' ? (
                <RiskLevelIcon />
              ) : alert.type === 'attention' ? (
                <AttentionIcon />
              ) : alert.type === 'positive' ? (
                <PositiveIcon />
              ) : alert.type === 'unknown' ? (
                <UnknownIcon />
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

interface Props {
  activeToken: TokenResult;
  isOpen: boolean;
  onOpenChange: Dispatch<SetStateAction<boolean>>;
}

export const SecurityModal = ({ activeToken, isOpen, onOpenChange }: Props) => {
  const { data: solanaSecurityData } = useSolanaTokenSecurityInfo({
    tokenAddress: activeToken.chainId === CHAIN_IDS.SOLANA ? activeToken.address : undefined,
  });

  const { accessControls, externalDependencies, issuanceInformation, customBehaviors } =
    solanaSecurityData
      ? calculateSolanaSecurityAlerts(solanaSecurityData)
      : calculateSecurityAlerts(activeToken);

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => onOpenChange(false)}
      onOpenChange={onOpenChange}
      variant="LARGE"
    >
      <div className="text-token-gray-1600 h-full overflow-hidden flex flex-col">
        <div className="pb-2 pt-6 px-4 tablet:px-6 sticky top-0 z-10 w-full">
          <div className="mb-4 flex justify-between w-full">
            <Dialog.Title
              className={fontStyles({ variant: 'H5', class: ['text-token-gray-1600'] })}
            >
              Security Audit
            </Dialog.Title>
            <Dialog.Description hidden>
              Security audit details for {activeToken.name} ({activeToken.symbol})
            </Dialog.Description>
            <Dialog.Close
              className={buttons({
                size: 'small',
                class: ['bg-transparent', '-mt-2', '-mr-2'],
                color: 'gray',
                shape: 'rounded',
              })}
              aria-label="close security audit dialog"
            >
              <XIcon className="size-4" />
            </Dialog.Close>
          </div>
        </div>

        <div className="overflow-y-auto overflow-x-hidden px-4 tablet:px-6">
          <div className="flex items-center pb-2 gap-1">
            {/* We currently support dynamic image URLS for solana */}
            {/* eslint-disable-next-line */}
            <img
              src={activeToken.logo ?? ''} // add backup icon?
              alt={`${activeToken.symbol} logo`}
              width={16}
              height={16}
              className="rounded-[50%] w-4 h-4 shrink"
            />
            <div className="flex items-center justify-center">
              <span
                className={fontStyles({
                  variant: 'LABEL_MD',
                  class: ['inline-flex', 'text-token-gray-1600'],
                })}
              >
                {activeToken.name}
                <span
                  className={fontStyles({
                    variant: 'CAPTION',
                    class: ['inline-flex', 'text-token-gray-800', 'mt-[1px]', 'ml-1'],
                  })}
                >
                  {activeToken.symbol}
                </span>
              </span>
            </div>
          </div>
          <div
            className={fontStyles({ variant: 'BODY_SM', class: ['pt-2', 'text-token-gray-800'] })}
          >
            Identifies risks present in the token’s code. Audit results are generated by{' '}
            <a
              href="https://gopluslabs.io/"
              target="_blank"
              className="text-token-purple-900 hover:underline"
            >
              Go+ Security
            </a>{' '}
            and are not verified by Matcha. Matcha does not guarantee accuracy. Do your own research
            before making any purchase decisions.
          </div>

          <AlertSection
            activeToken={activeToken}
            title="External Dependencies"
            alerts={externalDependencies}
          />
          <AlertSection
            activeToken={activeToken}
            title="Access Controls & Privileges"
            alerts={accessControls}
          />
          <AlertSection
            activeToken={activeToken}
            title="Issuance Information"
            alerts={issuanceInformation}
          />
          <AlertSection
            activeToken={activeToken}
            title="Custom Behaviors"
            alerts={customBehaviors}
          />
        </div>
      </div>
    </Modal>
  );
};

const getSourceContractLink = (token: TokenResult) => {
  switch (token.chainId) {
    case CHAIN_IDS.MAINNET:
      return { href: `https://etherscan.io/token/${token.address}#code`, label: 'Etherscan' };
    case CHAIN_IDS.BASE:
      return { href: `https://basescan.org/token/${token.address}#code`, label: 'BaseScan' };
    case CHAIN_IDS.BSC:
      return { href: `https://bscscan.com/token/${token.address}#code`, label: 'BscScan' };
    case CHAIN_IDS.MATIC:
      return { href: `https://polygonscan.com/token/${token.address}#code`, label: 'PolygonScan' };
    case CHAIN_IDS.AVALANCHE:
      return { href: `https://snowtrace.io/token/${token.address}#code`, label: 'SnowTrace' };
    case CHAIN_IDS.ARBITRUM:
      return { href: `https://arbiscan.io/token/${token.address}#code`, label: 'ArbiScan' };
    case CHAIN_IDS.OPTIMISM:
      return {
        href: `https://optimistic.etherscan.io/token/${token.address}#code`,
        label: 'Etherscan',
      };
    case CHAIN_IDS.SOLANA:
      return { href: `https://solscan.io/token/${token.address}#code`, label: 'SolScan' };
    case CHAIN_IDS.SCROLL:
      return { href: `https://scrollscan.com/token/${token.address}#code`, label: 'ScrollScan' };
    case CHAIN_IDS.LINEA:
      return { href: `https://lineascan.build/token/${token.address}#code`, label: 'LineaScan' };
    case CHAIN_IDS.MANTLE:
      return {
        href: `https://explorer.mantle.xyz/token/${token.address}#code`,
        label: 'MantleScan',
      };
    default:
      return { href: `https://etherscan.io/token/${token.address}#code`, label: 'Etherscan' };
  }
};
